import { Arrow } from "@radix-ui/react-popover";
import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { Button } from "../Button";
import { Popover, PopoverAnchor, PopoverContent } from "../Popover";

export type SpotlightProps = {
  /** The control to highlight. Focus returns to its first button or link. */
  children: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  dismissLabel?: string;
};

/** A single, dismissible introduction to a control, with modal focus handling. */
export function Spotlight({
  children,
  open,
  onOpenChange,
  title,
  description,
  dismissLabel = "Got it",
}: SpotlightProps) {
  const anchor = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const [bounds, setBounds] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
    viewportWidth: number;
    viewportHeight: number;
  } | null>(null);

  useLayoutEffect(() => {
    const element = anchor.current;
    if (!open || !element) return;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      setBounds({
        left: rect.left - 5,
        top: rect.top - 5,
        width: rect.width + 10,
        height: rect.height + 10,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open]);

  return (
    <Popover open={open} onOpenChange={onOpenChange} modal>
      <PopoverAnchor asChild>
        <div ref={anchor} className="inline-flex min-w-0 max-w-full">
          {children}
        </div>
      </PopoverAnchor>
      {open &&
        bounds &&
        createPortal(
          <div
            aria-hidden="true"
            className="pointer-events-none fixed inset-0 z-[60]"
          >
            <svg className="text-scrim/50 size-full">
              <path
                fill="currentColor"
                fillRule="evenodd"
                d={`M0 0H${bounds.viewportWidth}V${bounds.viewportHeight}H0Z M${bounds.left} ${bounds.top}h${bounds.width}v${bounds.height}h${-bounds.width}Z`}
              />
            </svg>
            <div
              className="ring-highlight absolute rounded-lg ring-2"
              style={{
                left: bounds.left,
                top: bounds.top,
                width: bounds.width,
                height: bounds.height,
              }}
            />
          </div>,
          document.body,
        )}
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={16}
        collisionPadding={16}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className="z-[70] w-80 max-w-[calc(100vw-2rem)] rounded-lg p-5"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          anchor.current
            ?.querySelector<HTMLElement>(
              "button, a[href], input, select, textarea, [tabindex='0']",
            )
            ?.focus();
        }}
      >
        <Arrow className="fill-surface" width={16} height={8} />
        <h2 id={titleId} className="text-body-lg font-semibold">
          {title}
        </h2>
        {description && (
          <p id={descriptionId} className="text-body text-text-secondary mt-2">
            {description}
          </p>
        )}
        <div className="mt-5 flex justify-end">
          <Button size="sm" onClick={() => onOpenChange(false)}>
            {dismissLabel}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
